import React, { memo, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface ThreadModerationListItemProps {
  thread: CorkBoardThreadSummary;
  isMutating: boolean;
  onPin: (thread: CorkBoardThreadSummary) => void;
  onLock: (thread: CorkBoardThreadSummary) => void;
  onEditTags: (thread: CorkBoardThreadSummary) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
}

export const ThreadModerationListItem: React.FC<ThreadModerationListItemProps> = memo(
  ({ thread, isMutating, onPin, onLock, onEditTags, onDelete }) => {
    const tagDots = useMemo(
      () =>
        thread.tags.slice(0, 3).map((tag) => (
          <View
            key={tag.id}
            style={[styles.tagDot, { backgroundColor: tag.color || '#6B7280' }]}
          />
        )),
      [thread.tags],
    );

    const formattedDate = useMemo(() => {
      const date = new Date(thread.createdAt);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }, [thread.createdAt]);

    return (
      <View style={styles.container}>
        {/* Top row: tags + title + badges */}
        <View style={styles.topRow}>
          <View style={styles.tagDots}>{tagDots}</View>
          <Text style={styles.title} numberOfLines={1}>
            {thread.title}
          </Text>
          <View style={styles.badges}>
            {thread.isAnnouncement && (
              <View style={[styles.badge, styles.announcementBadge]}>
                <Text style={styles.badgeText}>Announcement</Text>
              </View>
            )}
            {thread.isPinned && (
              <View style={[styles.badge, styles.pinnedBadge]}>
                <Text style={styles.badgeText}>Pinned</Text>
              </View>
            )}
            {thread.isLocked && (
              <View style={[styles.badge, styles.lockedBadge]}>
                <Text style={styles.badgeText}>Locked</Text>
              </View>
            )}
          </View>
        </View>

        {/* Meta row */}
        <View style={styles.meta}>
          <Text style={styles.metaText} numberOfLines={1}>{thread.creator.name}</Text>
          <Text style={styles.metaSeparator}>·</Text>
          <Text style={styles.metaText}>{formattedDate}</Text>
          <Text style={styles.metaSeparator}>·</Text>
          <Text style={styles.metaText}>{thread.replyCount} replies</Text>
          <Text style={styles.metaSeparator}>·</Text>
          <Text style={styles.metaText}>{thread.viewCount} views</Text>
        </View>

        {/* Actions row */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => onPin(thread)}
            disabled={isMutating}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={`${thread.isPinned ? 'Unpin' : 'Pin'} thread: ${thread.title}`}
          >
            <Text style={[styles.actionText, isMutating && styles.actionTextDisabled]}>
              {thread.isPinned ? 'Unpin' : 'Pin'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => onLock(thread)}
            disabled={isMutating}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={`${thread.isLocked ? 'Unlock' : 'Lock'} thread: ${thread.title}`}
          >
            <Text style={[styles.actionText, isMutating && styles.actionTextDisabled]}>
              {thread.isLocked ? 'Unlock' : 'Lock'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => onEditTags(thread)}
            disabled={isMutating}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={`Edit tags for thread: ${thread.title}`}
          >
            <Text style={[styles.actionText, isMutating && styles.actionTextDisabled]}>Tags</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => onDelete(thread)}
            disabled={isMutating}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={`Delete thread: ${thread.title}`}
          >
            <Text style={[styles.deleteText, isMutating && styles.actionTextDisabled]}>Delete</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  },
);

ThreadModerationListItem.displayName = 'ThreadModerationListItem';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tagDots: {
    flexDirection: 'row',
    gap: 4,
  },
  tagDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  badges: {
    flexDirection: 'row',
    gap: 4,
    flexShrink: 0,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  announcementBadge: {
    backgroundColor: '#1D4ED8',
  },
  pinnedBadge: {
    backgroundColor: '#F59E0B',
  },
  lockedBadge: {
    backgroundColor: '#EF4444',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  metaSeparator: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  actionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  actionTextDisabled: {
    opacity: 0.4,
  },
  deleteText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.ERROR,
  },
});
